import {clsx} from 'clsx'
import {CompactCommentButton} from '@github-ui/commenting/CompactCommentButton'
import {ReviewThreadComment} from '@github-ui/diff-lines/review-thread-comment'
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import type {FileKey, LineKey} from '@github-ui/diff-lines/types'
import {Button} from '@primer/react'
import {useResolveThreadMutation} from '../../../../mutations/use-resolve-thread-mutation'
import {useUnresolveThreadMutation} from '../../../../mutations/use-unresolve-thread-mutation'
import {CommentActionFileStats} from './CommentActionFileStats'
import type {DiffStats} from './comment-action-types'
import styles from './CommentActionCollapsed.module.css'

type CommentActionCollapsedProps = {
  filePath: string | undefined
  lineKey: LineKey | FileKey
  diffStats: DiffStats
  diffLines: NonNullable<Thread['subject']>['diffLines'] | undefined
  comment: NonNullable<NonNullable<Thread['commentsData']>['comments']>[number]
  commentIndex: number
  thread: Thread
  onReply: () => void
}

export const CommentActionCollapsed = ({
  filePath,
  lineKey,
  diffStats,
  diffLines,
  comment,
  commentIndex,
  thread,
  onReply,
}: CommentActionCollapsedProps) => {
  const {mutate: resolveThread, isPending: isResolving} = useResolveThreadMutation(filePath ?? '', lineKey)
  const {mutate: unresolveThread, isPending: isUnresolving} = useUnresolveThreadMutation(filePath ?? '', lineKey)

  const handleToggleResolve = () => {
    if (thread.isResolved) {
      unresolveThread({threadId: thread.id})
    } else {
      resolveThread({threadId: thread.id})
    }
  }

  return (
    <div className="d-flex flex-column">
      <CommentActionFileStats filePath={filePath} diffStats={diffStats} />
      <StaticUnifiedDiffPreview className="m-0 border-0" diffLines={diffLines} tabSize={4} />
      <div className={styles.CommentClamp}>
        <ReviewThreadComment
          lineMarkersKey={lineKey}
          key={comment.id}
          hideActions
          isInlineComment={false}
          // hack
          comment={{...comment, reactionGroups: undefined}}
          filePath={filePath ?? ''}
          index={commentIndex}
          isFirstComment={commentIndex === 0}
          isLastChild
          isOutdated={thread.isOutdated}
          isThreadResolved={thread?.isResolved ?? false}
          threadId={thread.id}
          originalDiffPathUri={thread.originalDiffPathUri}
          headingLevel="h3"
          tooltipDirection="sw"
        />
      </div>
      <div className={clsx('d-flex flex-row gap-4 px-2 pb-2', commentIndex !== 0 && 'bgColor-muted rounded-2')}>
        <CompactCommentButton onClick={onReply}>Write a reply</CompactCommentButton>
        <Button variant="default" onClick={handleToggleResolve} disabled={isResolving || isUnresolving}>
          {thread.isResolved ? 'Unresolve' : 'Resolve'}
        </Button>
      </div>
    </div>
  )
}
